import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Truck, Clock, MapPin, Wrench, CaretDown, CaretUp, WarningCircle, Spinner } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const DEPOT_ADDRESS = "264 bd du bon puits, 06210 Mandelieu-la-Napoule";

const ONSITE_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1h 00' },
  { value: 75, label: '1h 15' },
  { value: 90, label: '1h 30' },
  { value: 105, label: '1h 45' },
  { value: 120, label: '2h 00' },
  { value: 135, label: '2h 15' },
  { value: 150, label: '2h 30' },
];

function formatTime(isoStr) {
  if (!isoStr) return '--:--';
  const d = new Date(isoStr);
  return `${d.getHours()}h${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatMinutes(min) {
  if (!min && min !== 0) return '--';
  if (min >= 60) return `${Math.floor(min/60)}h${String(min%60).padStart(2,'0')}`;
  return `${min} min`;
}

export default function TimeEstimationPanel({
  technicianId,
  tireSource,
  clientAddress,
  interventionType,
  tireQuantity,
  onEstimationChange
}) {
  const [calculating, setCalculating] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [departureSource, setDepartureSource] = useState('depot');
  const [customDepartureAddress, setCustomDepartureAddress] = useState('');
  const [techAvailability, setTechAvailability] = useState(null);
  const [travelData, setTravelData] = useState(null);
  const [onsiteDuration, setOnsiteDuration] = useState(45);
  const [warning, setWarning] = useState('');

  // Auto-set onsite duration based on type and quantity
  useEffect(() => {
    if (interventionType === 'Réparation crevaison') {
      setOnsiteDuration(30);
    } else {
      const qty = parseInt(tireQuantity) || 2;
      setOnsiteDuration(qty <= 2 ? 45 : 90);
    }
  }, [interventionType, tireQuantity]);

  // Fetch technician availability
  useEffect(() => {
    if (!technicianId) { setTechAvailability(null); return; }
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/api/technicians/${technicianId}/next-availability`, { headers: getAuthHeaders() });
        setTechAvailability(r.data);
      } catch { setTechAvailability(null); }
    };
    fetch();
  }, [technicianId]);

  // Calculate travel when all inputs are ready
  const calculate = useCallback(async () => {
    if (!clientAddress) { setTravelData(null); return; }

    setCalculating(true);
    setWarning('');

    // Determine departure address:
    // 1) If the technician has a previous active intervention, use that intervention's address
    //    (the tech will be there after finishing the previous job).
    // 2) Otherwise, use the UI-selected source (depot or custom).
    let departureAddr;
    if (techAvailability && !techAvailability.available_now && techAvailability.current_address) {
      departureAddr = techAvailability.current_address;
    } else {
      departureAddr = departureSource === 'custom' ? customDepartureAddress : DEPOT_ADDRESS;
    }
    if (!departureAddr) { setCalculating(false); return; }

    // Determine tire pickup address
    let tirePickupAddr = null;
    if (tireSource && tireSource.startsWith('Fournisseur :')) {
      const parts = tireSource.replace('Fournisseur : ', '').split(' — ');
      if (parts.length > 1 && parts[1].trim()) {
        tirePickupAddr = parts[1].trim();
      }
    } else if (tireSource === 'Stock Reco Pneus') {
      tirePickupAddr = DEPOT_ADDRESS;
    }
    // "Autre" = no tire pickup leg

    try {
      const r = await axios.post(`${API}/api/calculate-travel`, {
        departure_address: departureAddr,
        tire_pickup_address: tirePickupAddr,
        client_address: clientAddress
      }, { headers: getAuthHeaders() });

      setTravelData(r.data);
      if (r.data.approximate) {
        setWarning('Calcul approximatif — API Google indisponible');
      }

      // Notify parent
      const departureTime = techAvailability?.next_available_at || new Date().toISOString();
      const arrivalTime = new Date(new Date(departureTime).getTime() + r.data.total_minutes * 60000).toISOString();
      const endTime = new Date(new Date(arrivalTime).getTime() + onsiteDuration * 60000).toISOString();

      onEstimationChange?.({
        travel_leg1_minutes: r.data.leg1.minutes,
        travel_leg2_minutes: r.data.leg2.minutes,
        total_travel_minutes: r.data.total_minutes,
        rounded_travel_minutes: r.data.rounded_travel_minutes,
        onsite_duration: onsiteDuration,
        estimated_departure: departureTime,
        estimated_arrival: arrivalTime,
        estimated_end: endTime,
        approximate: r.data.approximate
      });
    } catch (err) {
      setWarning(err.response?.data?.detail || 'Erreur de calcul');
    } finally {
      setCalculating(false);
    }
  }, [clientAddress, tireSource, departureSource, customDepartureAddress, techAvailability, onsiteDuration, onEstimationChange]);

  useEffect(() => {
    if (clientAddress && clientAddress.length > 5) {
      const timer = setTimeout(calculate, 1000);
      return () => clearTimeout(timer);
    }
  }, [clientAddress, tireSource, departureSource, customDepartureAddress, onsiteDuration, techAvailability]);

  // Also notify parent when onsite changes without recalculating travel
  useEffect(() => {
    if (travelData) {
      const departureTime = techAvailability?.next_available_at || new Date().toISOString();
      const arrivalTime = new Date(new Date(departureTime).getTime() + travelData.total_minutes * 60000).toISOString();
      const endTime = new Date(new Date(arrivalTime).getTime() + onsiteDuration * 60000).toISOString();
      onEstimationChange?.({
        travel_leg1_minutes: travelData.leg1.minutes,
        travel_leg2_minutes: travelData.leg2.minutes,
        total_travel_minutes: travelData.total_minutes,
        rounded_travel_minutes: travelData.rounded_travel_minutes,
        onsite_duration: onsiteDuration,
        estimated_departure: departureTime,
        estimated_arrival: arrivalTime,
        estimated_end: endTime,
        approximate: travelData.approximate
      });
    }
  }, [onsiteDuration]);

  const departureTime = techAvailability?.next_available_at || new Date().toISOString();
  const arrivalTime = travelData ? new Date(new Date(departureTime).getTime() + travelData.total_minutes * 60000).toISOString() : null;
  const endTime = arrivalTime ? new Date(new Date(arrivalTime).getTime() + onsiteDuration * 60000).toISOString() : null;

  return (
    <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-xl p-4" data-testid="time-estimation-panel">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        <Clock size={16} />
        Estimation des temps
        {calculating && <Spinner size={14} className="animate-spin text-reco-500" />}
      </h3>

      {/* Departure source (if tech has no active intervention) */}
      {techAvailability && techAvailability.available_now && (
        <div className="space-y-2">
          <Label className="text-gray-600 text-xs">Point de départ technicien</Label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDepartureSource('depot')}
              className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors ${
                departureSource === 'depot' ? 'border-reco-500 bg-reco-50 text-reco-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              Dépôt Mandelieu
            </button>
            <button
              type="button"
              onClick={() => setDepartureSource('custom')}
              className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors ${
                departureSource === 'custom' ? 'border-reco-500 bg-reco-50 text-reco-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              Adresse libre
            </button>
          </div>
          {departureSource === 'custom' && (
            <Input
              value={customDepartureAddress}
              onChange={(e) => setCustomDepartureAddress(e.target.value)}
              placeholder="Adresse de départ..."
              className="bg-white border-gray-300 text-sm"
            />
          )}
        </div>
      )}

      {/* Indicator when tech is busy — departure = previous intervention address */}
      {techAvailability && !techAvailability.available_now && techAvailability.current_address && (
        <div className="bg-white border border-gray-200 rounded-lg p-2.5 text-xs text-gray-600" data-testid="prev-intervention-departure">
          <div className="flex items-center gap-1 text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">
            <MapPin size={12} /> Départ depuis intervention précédente
          </div>
          <div className="text-gray-900 truncate">{techAvailability.current_address}</div>
        </div>
      )}

      {/* Onsite duration */}
      <div className="space-y-2">
        <Label className="text-gray-600 text-xs">Temps estimé sur place</Label>
        <Select value={String(onsiteDuration)} onValueChange={(v) => setOnsiteDuration(parseInt(v))}>
          <SelectTrigger className="bg-white border-gray-300 h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ONSITE_OPTIONS.map(o => (
              <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Warning */}
      {warning && (
        <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg border border-amber-200">
          <WarningCircle size={14} />
          {warning}
        </div>
      )}

      {/* Calculated results */}
      {travelData && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <CalcField icon={<Truck size={14} />} label="Trajet estimé" value={formatMinutes(travelData.rounded_travel_minutes)} />
            <CalcField icon={<Clock size={14} />} label="Départ technicien" value={formatTime(departureTime)} />
            <CalcField icon={<MapPin size={14} />} label="Arrivée client" value={formatTime(arrivalTime)} />
            <CalcField icon={<Wrench size={14} />} label="Fin estimée" value={formatTime(endTime)} />
          </div>

          {/* Detail panel */}
          <button
            type="button"
            onClick={() => setDetailOpen(!detailOpen)}
            className="flex items-center gap-1 text-xs text-reco-600 hover:text-reco-700 font-medium mt-1"
          >
            {detailOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
            {detailOpen ? 'Masquer le détail' : 'Voir le détail'}
          </button>

          {detailOpen && (
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs space-y-1.5 text-gray-600">
              <div className="flex items-center gap-2">
                <span>🚗</span> Trajet dépôt/fournisseur : <strong>{travelData.leg1.minutes} min</strong>
              </div>
              <div className="flex items-center gap-2">
                <span>🚗</span> Trajet vers client : <strong>{travelData.leg2.minutes} min</strong>
              </div>
              <div className="flex items-center gap-2">
                <span>🔧</span> Temps sur place : <strong>{formatMinutes(onsiteDuration)}</strong>
              </div>
              <div className="border-t border-gray-100 pt-1.5 mt-1.5">
                <div className="flex items-center gap-2">
                  <span>🕐</span> Départ estimé : <strong>{formatTime(departureTime)}</strong>
                </div>
                <div className="flex items-center gap-2">
                  <span>🕐</span> Arrivée estimée : <strong>{formatTime(arrivalTime)}</strong>
                </div>
                <div className="flex items-center gap-2">
                  <span>🕐</span> Fin estimée : <strong>{formatTime(endTime)}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!travelData && !calculating && clientAddress && (
        <p className="text-xs text-gray-400">Renseignez l'adresse client pour calculer les temps</p>
      )}
    </div>
  );
}

function CalcField({ icon, label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2.5">
      <div className="flex items-center gap-1 text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">
        {icon} {label}
      </div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}
